import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { isEmpty } from 'lodash-es';
import {
  computed,
  nextTick,
  ref,
  toRef,
} from 'vue';
import { useRoute } from 'vue-router';

import type { IStatusDescriptionOptions } from '@modules/affiliates-profile/composables';
import type {
  IAffiliateDataFormSchema,
  IAffiliateProfileFormSchema,
  IAffiliateProfileGeneralPayload,
  IAffiliateProfileResponse,
} from '@modules/affiliates-profile/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { InputMessageSeverity } from '@modules/forms/types';
import { AFFILIATES_APPROVED_PERMISSIONS, AFFILIATES_LIST_QUERY_KEY } from '@modules/affiliates-approved/constants';
import { useAffiliatesDepartmentListQuery } from '@modules/affiliates-approved/queries';
import { TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL } from '@modules/affiliates-approved/submodules/affiliate-profile/submodules/general/constants';
import { TRANSLATION_GROUP_AFFILIATES_PENDING } from '@modules/affiliates-pending/constants';
import { useAffiliateProfileForms, useStatusDescription } from '@modules/affiliates-profile/composables';
import { AFFILIATE_PROFILE_GET_QUERY_KEY } from '@modules/affiliates-profile/constants';
import { useAffiliatesProfileGetQuery } from '@modules/affiliates-profile/queries';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import { EAasmState } from '@modules/core/enums';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { useToastStore } from '@modules/toast/store';
import { useQueryClient } from '@tanstack/vue-query';
import {
  assert,
  isDate,
  isNumber,
  isString,
} from '@utils/guards';
import { formatDate } from '@vueuse/core';

import { AFFILIATES_APPROVED_PROFILE_PERMISSIONS } from '../constants';
import { useAffiliateProfileResetTwoFactorAuthMutation, useAffiliateProfileSubaffiliateMutation, useAffiliateProfileUpdateMutation } from '../mutations';

export interface AffiliateProfileUpdateComposable {
  formGeneralSchema: ComputedRef<IAffiliateProfileFormSchema>;
  contactsSchema: ComputedRef<IAffiliateProfileFormSchema>;
  subaffiliateSchema: ComputedRef<IAffiliateProfileFormSchema>;
  profileErrors: Ref<IApiFormErrors | undefined>;
  subaffiliateErrors: Ref<IApiFormErrors | undefined>;
  isOptionsLoading: Ref<boolean | undefined>;
  isProfileUpdateLoading: Ref<boolean | undefined>;
  isSubaffiliateUpdateLoading: Ref<boolean | undefined>;
  clearProfileError(key: string): void;
  clearSubaffiliateError(key: string): void;
  onSubmitGeneral(values: IAffiliateDataFormSchema): Promise<void>;
  onSubmitContacts(values: IAffiliateDataFormSchema): Promise<void>;
  onSubmitSubaffiliate(values: IAffiliateDataFormSchema): Promise<void>;
  onChange(values: IAffiliateDataFormSchema): void;
}

function updateContactsQueryData(queryData: IAffiliateProfileResponse | undefined, values: IAffiliateProfileGeneralPayload): IAffiliateProfileResponse | undefined {
  if (!queryData) {
    return queryData;
  }

  return {
    ...queryData,
    affiliate: {
      ...queryData.affiliate,
      ...values,
    },
  };
}

export default function useAffiliateProfileUpdate(commonFormTranslationKey: MaybeRefOrGetter<symbol>, isStatusEnabled?: Ref<boolean>): AffiliateProfileUpdateComposable {
  const showDepartmentWarning = ref(false);

  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();
  const route = useRoute();
  const { displayPresetToast } = useToastStore();
  const { formatMonthDate } = useFormatMonthDate();
  const queryClient = useQueryClient();

  const affiliateId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

  const updateDepartmentPermission = computed<boolean>(() => hasPermission(AFFILIATES_APPROVED_PERMISSIONS.UPDATE_DEPARTMENT_FOR_AFFILIATES).value);

  const { data: options, isPending: isOptionsLoading, setData } = useAffiliatesProfileGetQuery(affiliateId);
  const { data: departments, isPending: isDepartmentsLoading } = useAffiliatesDepartmentListQuery(updateDepartmentPermission);

  const {
    mainSchema,
    contactsSchema,
    offerSchema,
    subaffiliateSchema,
  } = useAffiliateProfileForms(options, toRef(commonFormTranslationKey));

  const {
    mutateAsync: updateProfile,
    isPending: isProfileUpdateLoading,
    errors: profileErrors,
    clearError: clearProfileError,
  } = useAffiliateProfileUpdateMutation();

  const {
    mutateAsync: updateSubaffiliate,
    isPending: isSubaffiliateUpdateLoading,
    errors: subaffiliateErrors,
    clearError: clearSubaffiliateError,
  } = useAffiliateProfileSubaffiliateMutation();

  const { mutateAsync: resetTwoFactorAuth } = useAffiliateProfileResetTwoFactorAuthMutation();

  const affiliate = computed(() => options.value?.affiliate);
  const roles = computed(() => options.value?.roles ?? []);
  const chiefs = computed(() => options.value?.chiefs ?? []);

  const canResetTwoFactorAuth = computed(() => hasPermission(AFFILIATES_APPROVED_PROFILE_PERMISSIONS.CLEAR_GOOGLE_SECRET).value);

  const affiliateStatusOptions = computed<IStatusDescriptionOptions>(() => ({
    date: affiliate.value?.ban_status_chaged_at,
    name: affiliate.value?.ban_status_name,
    translationKey: TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL,
    shortCode: 'MAIN_STATUS_OWNER',
    dateProp: 'ban_status_chaged_at',
    nameProp: 'ban_status_name',
  }));

  const { description } = useStatusDescription(affiliateStatusOptions);

  const isCustomDepartment = computed<boolean>(() => (
    affiliate.value?.department_id != null
    && Array.isArray(departments.value)
    && departments.value.find((item) => affiliate.value?.department_id === item.id)?.aasm_state !== EAasmState.DEFAULT
  ));

  const departmentDescriptionSeverity = computed<InputMessageSeverity>(() => isCustomDepartment.value ? 'info' : 'warn');

  const departmentDescriptionText = computed(() => {
    if (isCustomDepartment.value) {
      return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_DEPARTMENT_INFO').value;
    }
    return showDepartmentWarning.value ? getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_SAVE_DEPARTMENT_WARNING').value : undefined;
  });

  const formGeneralSchema = computed<IAffiliateProfileFormSchema>(() => {
    let schema: IAffiliateProfileFormSchema = {
      ...mainSchema.value,
      role_id: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_ROLE').value,
        value: affiliate.value?.role_id,
        options: roles.value,
        optionValue: 'id',
        optionLabel: 'name',
      },
      chief_id: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_CHIEF').value,
        value: affiliate.value?.chief_id,
        options: chiefs.value,
        optionValue: 'id',
        optionLabel: 'name',
      },
    };

    if (updateDepartmentPermission.value) {
      schema = {
        ...schema,
        department_id: {
          type: EFormInputTypes.SELECT,
          options: departments.value ?? [],
          label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_DEPARTMENT').value,
          value: affiliate.value?.department_id,
          optionValue: 'id',
          optionLabel: 'name',
          loading: isDepartmentsLoading.value,
          readonly: isCustomDepartment.value,
          description: departmentDescriptionText.value,
          descriptionSeverity: departmentDescriptionSeverity.value,
        },
      };
    }

    if (canResetTwoFactorAuth.value) {
      schema = {
        ...schema,
        mfa_enabled: {
          type: EFormInputTypes.SWITCH,
          label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_MFA_ENABLED_LABEL').value,
          value: affiliate.value?.mfa_enabled,
          disabled: !affiliate.value?.mfa_enabled,
          trueLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_MFA_ENABLED').value,
          falseLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_MFA_DISABLED').value,
        },
      };
    }

    if (isStatusEnabled?.value) {
      schema = {
        ...schema,
        banned: {
          type: EFormInputTypes.SWITCH,
          label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_STATUS_LABEL').value,
          value: affiliate.value?.banned,
          description: affiliate.value?.ban_status_name ? description.value : undefined,
          trueLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_STATUS_BANNED').value,
          falseLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_STATUS_UNBANNED').value,
        },
      };
    }

    schema = {
      ...schema,
      ...offerSchema.value,
      admin_note: {
        type: EFormInputTypes.TEXTAREA,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_GENERAL, 'MAIN_ADMIN_NOTE').value,
        value: affiliate.value?.admin_note,
      },
    };

    return schema;
  });

  async function refetchData(): Promise<void> {
    await queryClient.invalidateQueries({
      predicate: (query) => isString(query.queryKey[0]) && [
        AFFILIATES_LIST_QUERY_KEY,
        AFFILIATE_PROFILE_GET_QUERY_KEY,
      ].includes(query.queryKey[0]),
    });

    displayPresetToast('edited');
  }

  function onChange(values: IAffiliateDataFormSchema): void {
    showDepartmentWarning.value = values.department_id !== affiliate.value?.department_id && !isCustomDepartment.value;
  }

  async function onSubmitGeneral({ mfa_enabled, ...values }: IAffiliateDataFormSchema): Promise<void> {
    assert(isNumber(affiliateId.value));

    try {
      await updateProfile({
        id: affiliateId.value,
        affiliate: {
          ...values,
          birthday: isDate(values.birthday) ? formatDate(values.birthday, RESPONSE_DATE_FORMAT) : values.birthday,
        },
      });

      if (canResetTwoFactorAuth.value && !mfa_enabled && affiliate.value?.mfa_enabled) {
        await resetTwoFactorAuth(affiliateId.value);
      }
      await refetchData();
    } catch {}
  }

  async function onSubmitContacts(values: IAffiliateDataFormSchema): Promise<void> {
    assert(isNumber(affiliateId.value));

    try {
      const response = await updateProfile({
        id: affiliateId.value,
        affiliate: {
          ...values,
          birthday: isDate(values.birthday) ? formatDate(values.birthday, RESPONSE_DATE_FORMAT) : values.birthday,
        },
      });

      if (!isEmpty(response?.affiliate)) {
        // Server response can be the same as previous one and vue does not update form, so first of all set payload as query data
        setData((queryData) => {
          return updateContactsQueryData(queryData, { ...values, birthday: response?.affiliate?.birthday });
        });
        await nextTick();
        // Then set server response to trigger vue reactivity
        setData((queryData) => {
          return updateContactsQueryData(queryData, response?.affiliate);
        });
      }
      await refetchData();
    } catch {}
  }

  async function onSubmitSubaffiliate({ affiliate_invitation_id, date }: IAffiliateDataFormSchema): Promise<void> {
    assert(isNumber(affiliateId.value));

    if (!date)
      return;

    try {
      await updateSubaffiliate({
        id: affiliateId.value,
        affiliate_invitation: {
          affiliate_id: affiliate_invitation_id,
          date: formatMonthDate(date),
        },
      });
      await refetchData();
    } catch {}
  }

  return {
    formGeneralSchema,
    contactsSchema,
    subaffiliateSchema,
    profileErrors,
    subaffiliateErrors,
    isOptionsLoading,
    isProfileUpdateLoading,
    isSubaffiliateUpdateLoading,
    clearProfileError,
    clearSubaffiliateError,
    onSubmitGeneral,
    onSubmitContacts,
    onSubmitSubaffiliate,
    onChange,
  };
}
